/**
 * useTranslations — Namespaced i18n hook built on react-intl.
 *
 * Provides a `t('key')` API with automatic namespace prefixing.
 * Messages are flat: `t('title')` inside `useTranslations('home.hero')`
 * resolves to message ID `home.hero.title`.
 *
 * Keys are compile-time validated via MessageKeys<N> from messages_gen.ts.
 * Calling t('nonExistentKey') is a TypeScript error.
 *
 * Also exports useLocale and useFormatter for convenience.
 */
import type { DynamicKeys, DynKey, MessageArgs, MessageKeys } from '@/shared/i18n/messages_gen';
import {
  DEFAULT_LOCALE,
  isLocale,
  type Locale,
  type LocalizedText,
} from '@letsgethome/types/branded';
import { useIntl, type PrimitiveType } from 'react-intl';

type TranslateValues = Record<string, string | number | boolean | Date | null | undefined>;

/** The full message id for a relative key under namespace `N` (`''` = no prefix). */
type FullKey<N extends string, K extends string> = N extends '' ? K : `${N}.${K}`;

/**
 * The `values` parameter of `t(key, …)` for a relative key `K` under namespace
 * `N`: REQUIRED + typed when the message has ICU arguments (`MessageArgs` from
 * the codegen, e.g. `{ count: number }`), OPTIONAL when it has none. So
 * `t('search.listingCount')` is a compile error (missing `{count}`), and
 * `t('home.hero.title')` needs no second argument.
 */
type ArgsParam<N extends string, K extends string> =
  MessageArgs<FullKey<N, K>> extends Record<string, never>
    ? [values?: TranslateValues]
    : [values: MessageArgs<FullKey<N, K>>];

/** `true` iff `T` is a union type (distributes `T`, compares against the whole). */
type IsUnion<T, U = T> = T extends unknown ? ([U] extends [T] ? false : true) : never;

/**
 * The compile-time guard behind `t.dyn`: reject a SINGLE string-LITERAL key.
 *
 * A key someone typed out verbatim (`t.dyn('calculator.title')`) infers as one
 * unit literal type — it is statically known, so it must use the typed `t()`
 * instead (this is the type-level replacement for the `no-dyn-literal-key`
 * ESLint rule on the public app). Genuinely runtime-built keys keep working:
 *   - a template over a number (`t.dyn(`faqs.list.${i}.question`)`) infers a
 *     TEMPLATE-LITERAL pattern type, matched against `DynamicKeys<N>`;
 *   - a value from typed data (`t.dyn(calc.titleKey)`, the reason→key map)
 *     infers a UNION of catalog keys — a value, not a hand-typed literal.
 *
 * NOTE: staff cannot adopt this — its `web_public ∪ web_staff` catalog trips
 * TS2590 ('union too complex') under the `DynKey` instantiation, so staff keeps
 * `dyn(key: string)` guarded by the `no-dyn-literal-key` lint instead.
 */
type NonLiteralDynKey<N extends string, K> =
  true extends IsUnion<K>
    ? unknown
    : [Extract<DynamicKeys<N>, K>] extends [never]
      ? never
      : unknown;

/**
 * Namespaced translate function. Parameterized by the NAMESPACE `N` (not the key
 * union) so the per-key ICU argument types can be resolved via `${N}.${key}`.
 */
export interface TranslateFunction<N extends string> {
  // Returns the branded `LocalizedText`: a translation is user-facing text that
  // is present in EVERY locale (the codegen parity gate), so it can flow into a
  // `LocalizedText`-typed prop while a raw string literal cannot — that is what
  // makes a hardcoded `title="For Rent"` a compile error.
  //
  // `this: void` on the accessors — they're closure arrows that never use
  // `this`, so extracting a bare reference (e.g. `const d = t.dyn`) is safe
  // (no unbound-method).
  <K extends MessageKeys<N> & string>(key: K, ...args: ArgsParam<N, K>): LocalizedText;
  rich(this: void, key: MessageKeys<N>, values?: Record<string, unknown>): LocalizedText;
  raw(this: void, key: MessageKeys<N>): LocalizedText;
  /**
   * Dynamic-key access for keys constructed from runtime data
   * (e.g. `t.dyn(`faqs.list.${i}.question`)`). The key is STILL
   * type-checked — `DynamicKeys<N>` is the set of template-literal
   * patterns the codegen derives from array indices + numbered
   * families, so a typo'd prefix/field is a compile error (only the
   * numeric index itself is unbounded). A hand-typed SINGLE literal
   * (`t.dyn('calculator.title')`) is a COMPILE error
   * ({@link NonLiteralDynKey}) — a static key must use the typed
   * `t(key)`; this hatch is for proven dynamic shapes, NOT arbitrary
   * strings.
   */
  dyn<K extends DynKey<N>>(
    this: void,
    key: K & NonLiteralDynKey<N, K>,
    values?: TranslateValues
  ): LocalizedText;
}

// Default `N = ''` (NOT `string`): a no-arg `useTranslations()` is the
// namespaceless translator and must be TYPED against the full catalog
// (`MessageKeys<''> = AllMessageKeys`). Defaulting to `string` made `t('key')`
// accept ANY string — an untyped hole that silently rendered missing keys.
export function useTranslations<N extends string = ''>(namespace?: N): TranslateFunction<N> {
  const intl = useIntl();

  // `useTranslations` IS the sanctioned `LocalizedText` mint (alongside
  // `plainText`): a formatted message is translatable user-facing text. The
  // `as LocalizedText` casts below are the one place the brand is applied to
  // `formatMessage`'s output — inline-disabled for the `no-localized-text-cast`
  // gate, exactly as the `@letsgethome/lib/images` builder mints `ImageUrl`.
  const mint = (id: string, values?: TranslateValues): LocalizedText =>
    // eslint-disable-next-line @letsgethome/no-localized-text-cast -- this hook is the sanctioned LocalizedText mint
    intl.formatMessage({ id }, values as Record<string, PrimitiveType>) as LocalizedText;

  const t = (key: string, values?: TranslateValues): LocalizedText =>
    mint(namespace ? `${namespace}.${key}` : key, values);

  t.rich = (key: string, values?: Record<string, unknown>): LocalizedText =>
    mint(namespace ? `${namespace}.${key}` : key, values as TranslateValues);

  t.raw = (key: string): LocalizedText => mint(namespace ? `${namespace}.${key}` : key);

  t.dyn = (key: string, values?: TranslateValues): LocalizedText =>
    mint(namespace ? `${namespace}.${key}` : key, values);

  // The runtime closure is structurally permissive; the precise per-key arg
  // typing lives entirely in `TranslateFunction<N>`'s call signature.
  return t as TranslateFunction<N>;
}

export function useLocale(): Locale {
  // `intl.locale` is react-intl's bare `string`. The `<IntlProvider>` is fed a
  // branded `Locale` (render.tsx, from `localeFromPath`), so this validates at
  // the single read seam — the sanctioned `string → Locale` crossing — and
  // every caller gets a `Locale` with no `as Locale` cast.
  const intl = useIntl();
  return isLocale(intl.locale) ? intl.locale : DEFAULT_LOCALE;
}
